/**
 * Billing-related utility functions and constants
 */

import { featureMap, FeatureType, PlanTypes, V2PlanTypes } from '~/types/features'
import { CompanySizeChoices, InvoiceStatus, OwnerBillingVersion } from '~/types/types'
import { formatDate } from '~/utils/date'

/** Default trial period duration in days */
export const TRIAL_PERIOD_DAYS = 14

/** Number of milliseconds in a day */
export const DAY_IN_MS = 24 * 60 * 60 * 1000

/** DeepSource sales contact URL */
export const SALES_URL = 'https://deepsource.com/contact/sales'

/** DeepSource docs page for migrating from a personal account to a team account */
export const PERSONAL_TO_TEAM_DOCS_URL =
  'https://docs.deepsource.com/docs/platform/dashboard/account/personal-to-team-migration'

/** Possible return values from getLegacyPlanType for V1 plan classification */
export enum LegacyPlanType {
  Free = 'free',
  Starter = 'starter',
  Business = 'business'
}

// V1 plan slug constants
const V1_SLUG_FREE = 'free'
const V1_SLUG_PREMIUM = 'premium'
const V1_SLUG_ENTERPRISE = 'enterprise'

// V2 (unified) plan slug constants
export const V2_SLUG_OPEN_SOURCE = 'uos'
export const V2_SLUG_TRIAL = 'utr'
export const V2_SLUG_TEAM = 'ut'
export const V2_SLUG_ENTERPRISE = 'uen'

const V2_SLUGS = new Set([V2_SLUG_OPEN_SOURCE, V2_SLUG_TRIAL, V2_SLUG_TEAM, V2_SLUG_ENTERPRISE])
const V2_PAID_SLUGS = new Set([V2_SLUG_TRIAL, V2_SLUG_TEAM, V2_SLUG_ENTERPRISE])

/**
 * Reasons the backend reports on the dashboard context's `subscription_pause_reason`
 * field, set alongside `is_subscription_paused`. Keep in sync with the asgard
 * `subscription_pause_reason` vocabulary.
 */
export type SubscriptionPauseReason = 'billing_past_due' | 'trial_ended'

/**
 * Value of `subscription_pause_reason` when a subscription is paused because payment
 * is past due (vs. a trial-ended pause, which carries a different reason).
 */
export const PAUSE_REASON_PAST_DUE: SubscriptionPauseReason = 'billing_past_due'

/**
 * Extract slug string from subscribed_plan_info, which can be a string or { slug: string } object.
 */
function extractSlug(planInfo: unknown): string | undefined {
  if (!planInfo) return undefined
  if (typeof planInfo === 'object' && planInfo !== null && 'slug' in planInfo) {
    return (planInfo as Record<string, unknown>).slug as string | undefined
  }
  if (typeof planInfo === 'string') return planInfo
  return undefined
}

/**
 * Checks if a plan is a unified trial plan based on its slug
 * @param planInfo - The plan info object to check
 * @returns true if the plan is a unified trial plan, false otherwise
 */
export function isUnifiedTrialPlan(planInfo: unknown): boolean {
  const slug = extractSlug(planInfo)
  return slug === V2_SLUG_TRIAL || slug === 'trial'
}

/**
 * Returns the image path for a payment card brand.
 * Falls back to an unknown card image if the brand is not recognized.
 */
export function getCardImage(brand: string): string {
  try {
    return require(`~/assets/images/payments/${brand}.png`)
  } catch {
    return require('~/assets/images/payments/unknown.png')
  }
}

/** Convert cents (integer) to dollars (number). */
export function centsToDollars(cents: number): number {
  return cents / 100
}

/** Convert a dollar string to cents (integer). Returns 0 for invalid input. */
export function dollarsToCents(dollars: string): number {
  const parsed = parseFloat(dollars)
  return isFinite(parsed) ? Math.round(parsed * 100) : 0
}

/**
 * Returns true if the plan is a V2 (unified billing) plan.
 */
export function isUnifiedBillingPlan(planInfo: unknown): boolean {
  const slug = extractSlug(planInfo)
  return slug !== undefined && V2_SLUGS.has(slug)
}

/**
 * Returns true for Business/Enterprise-tier plans on both V1 and V2 billing.
 * V1: 'enterprise', 'premium'
 * V2: 'ut' (team), 'uen' (enterprise)
 */
export function isBusinessOrEnterprisePlan(planInfo: unknown): boolean {
  const slug = extractSlug(planInfo)
  if (!slug) return false
  return (
    slug === V1_SLUG_ENTERPRISE ||
    slug === V1_SLUG_PREMIUM ||
    slug === V2_SLUG_TRIAL || // Unified trial is considered equivalent to team/enterprise for feature gating
    slug === V2_SLUG_TEAM ||
    slug === V2_SLUG_ENTERPRISE
  )
}

/**
 * Returns true only for Enterprise-tier plans on both V1 and V2 billing.
 * Used for SSO gating.
 * V1: 'enterprise'
 * V2: 'uen'
 */
export function isEnterprisePlan(planInfo: unknown): boolean {
  const slug = extractSlug(planInfo)
  return slug === V1_SLUG_ENTERPRISE || slug === V2_SLUG_ENTERPRISE
}

/**
 * Returns true for free-tier plans on both V1 and V2 billing.
 * V1: 'free'
 * V2: 'uos' (open source)
 */
export function isFreePlan(planInfo: unknown): boolean {
  const slug = extractSlug(planInfo)
  return slug === V1_SLUG_FREE || slug === V2_SLUG_OPEN_SOURCE
}

/**
 * Classifies a V1 plan into free/starter/business. Returns null for V2 plans.
 * V1 business: 'premium', 'enterprise'
 * V1 free: 'free'
 * V1 starter: all other V1 slugs (e.g. 'starter', 'ps')
 */
export function getLegacyPlanType(planInfo: unknown): LegacyPlanType | null {
  if (isUnifiedBillingPlan(planInfo)) return null
  const slug = extractSlug(planInfo)
  if (slug === V1_SLUG_PREMIUM || slug === V1_SLUG_ENTERPRISE) return LegacyPlanType.Business
  if (slug === V1_SLUG_FREE) return LegacyPlanType.Free
  if (!slug) return null
  return LegacyPlanType.Starter
}

/**
 * Returns true for paid V2 (unified billing) plans: Trial, Team, Enterprise.
 * Excludes V1 plans and V2 Open Source.
 */
export function isV2PaidPlan(planInfo: unknown): boolean {
  const slug = extractSlug(planInfo)
  return slug !== undefined && V2_PAID_SLUGS.has(slug)
}

/**
 * Returns true if the owner should see the V2 billing experience.
 * True for V2 owners, and also for V1 owners on the free plan.
 */
export function shouldShowV2Experience(
  owner:
    | {
        billingVersion?: OwnerBillingVersion | null
        hasPremiumPlan?: boolean | null
      }
    | null
    | undefined
): boolean {
  if (!owner) return false
  if (owner.billingVersion === OwnerBillingVersion.V2) return true
  if (!owner.hasPremiumPlan) return true
  return false
}

/**
 * Returns true if the user's plan grants access to the given feature.
 * Checks minRequiredPlanV2 for V2 billing users, minRequiredPlan for V1 users.
 * Features with only minRequiredPlanV2 are inaccessible on V1 billing.
 */
export function canAccessFeature(feature: FeatureType, planInfo: unknown): boolean {
  const config = featureMap[feature]
  if (!config) return true

  if (isUnifiedBillingPlan(planInfo)) {
    switch (config.minRequiredPlanV2) {
      case V2PlanTypes.TEAM:
        return isV2PaidPlan(planInfo)
      case V2PlanTypes.ENTERPRISE:
        return isEnterprisePlan(planInfo)
      default:
        return true
    }
  }

  // V1 billing
  if (!config.minRequiredPlan) {
    // V2-only feature — not available on V1
    return !config.minRequiredPlanV2
  }

  switch (config.minRequiredPlan) {
    case PlanTypes.FREE:
      return true
    case PlanTypes.STARTER:
      return !isFreePlan(planInfo)
    case PlanTypes.BUSINESS:
      return isBusinessOrEnterprisePlan(planInfo)
    case PlanTypes.ENTERPRISE:
      return isEnterprisePlan(planInfo)
    default:
      return true
  }
}

/**
 * Returns the display name for the minimum required plan, adapting to V1/V2 billing.
 * Shows V2 plan name (e.g. "Team") for unified billing users, V1 name (e.g. "Business") otherwise.
 */
export function getRequiredPlanName(feature: FeatureType, planInfo: unknown): string {
  const config = featureMap[feature]
  if (!config) return ''

  const rawName =
    isUnifiedBillingPlan(planInfo) && config.minRequiredPlanV2
      ? config.minRequiredPlanV2
      : (config.minRequiredPlan ?? config.minRequiredPlanV2)

  if (!rawName) return ''
  return rawName.charAt(0).toUpperCase() + rawName.slice(1)
}

/**
 * Returns the appropriate trial walkthrough URL based on company size.
 * Orgs with >25 developers get a Calendly scheduling link; others get the Contact Sales page.
 */
export function getTrialWalkthroughUrl(
  walkthroughUrl: string,
  companySize?: CompanySizeChoices | null
): string {
  if (
    walkthroughUrl &&
    companySize &&
    companySize !== CompanySizeChoices.UpTo_5 &&
    companySize !== CompanySizeChoices.Size_5_25
  ) {
    return walkthroughUrl
  }
  return SALES_URL
}

/** A saved card from a unified billing query, shaped for display in payment-method UI. */
export interface PaymentMethodView {
  id: string
  brand: string
  brandLabel: string
  lastFour: string
  expiryLabel: string
  expiryShort: string
  isDefault: boolean
}

/** The subset of a unified billing `Card` needed to build a {@link PaymentMethodView}. */
export interface UnifiedCardLike {
  id?: string | null
  brand?: string | null
  endingIn?: string | null
  expYear?: number | null
  expMonth?: number | null
  isDefault?: boolean | null
}

/**
 * Maps a unified billing card to the display shape used by the payment-method components.
 */
export function mapUnifiedCardToPaymentMethod(card: UnifiedCardLike): PaymentMethodView {
  const brand = card.brand ?? 'card'
  return {
    id: card.id ?? '',
    brand: brand.toLowerCase(),
    brandLabel: brand.charAt(0).toUpperCase() + brand.slice(1).toLowerCase(),
    lastFour: card.endingIn ?? '',
    expiryLabel:
      card.expMonth && card.expYear
        ? `Expires ${new Date(card.expYear, card.expMonth - 1).toLocaleString('en-US', {
            month: 'long'
          })} ${card.expYear}`
        : '',
    expiryShort:
      card.expMonth && card.expYear
        ? `${String(card.expMonth).padStart(2, '0')}/${String(card.expYear).slice(-2)}`
        : '',
    isDefault: card.isDefault ?? false
  }
}

/**
 * Display labels produced by {@link formatInvoiceStatus}. `''` denotes an unknown/absent
 * status. These exact strings are the contract consumed by `V2RecentInvoices.statusColor`,
 * so the two must stay in sync.
 */
export type InvoiceStatusLabel = 'Paid' | 'Open' | 'Draft' | 'Uncollectible' | 'Void' | ''

/** Human-readable label for an invoice status, e.g. `OPEN` -> `Open`. Empty string if unknown. */
export function formatInvoiceStatus(status: InvoiceStatus | null | undefined): InvoiceStatusLabel {
  switch (status) {
    case InvoiceStatus.Paid:
      return 'Paid'
    case InvoiceStatus.Open:
      return 'Open'
    case InvoiceStatus.Draft:
      return 'Draft'
    case InvoiceStatus.Uncollectible:
      return 'Uncollectible'
    case InvoiceStatus.Void:
      return 'Void'
    default:
      return ''
  }
}

/** An invoice from a unified billing query, shaped for display in the invoice-list UI. */
export interface InvoiceView {
  id: string
  date: string
  amount: number
  status: InvoiceStatusLabel
  url: string
}

/** The subset of a unified billing `Invoice` needed to build an {@link InvoiceView}. */
export interface UnifiedInvoiceLike {
  invoiceId?: string | null
  date?: string | null
  amountCents?: number | null
  status?: InvoiceStatus | null
  url?: string | null
}

/**
 * Maps a unified billing invoice to the display shape used by the invoice-list components.
 */
export function mapUnifiedInvoiceToView(invoice: UnifiedInvoiceLike): InvoiceView {
  return {
    id: invoice.invoiceId ?? '',
    date: invoice.date ? formatDate(invoice.date) : '',
    amount: centsToDollars(invoice.amountCents ?? 0),
    status: formatInvoiceStatus(invoice.status),
    url: invoice.url ?? ''
  }
}
